'use client';

import {
  type OneTrustEnvironment,
  OneTrustProvider,
} from '@jouzen/onetrust-consent-manager';
import { env } from 'next-runtime-env';
import type { ReactNode } from 'react';

import { logger } from '@/logger/logger.browser';

interface OneTrustConsentProviderEnvProps {
  environment?: OneTrustEnvironment;
  onetrustKey?: string;
  segmentKey?: string;
}

const OneTrustConsentProvider = ({
  children,
}: {
  readonly children: ReactNode;
}) => {
  // Returns an array of undefined properties from props
  const getMissingProps = (
    props: OneTrustConsentProviderEnvProps,
  ): string[] => {
    const missing: string[] = [];
    for (const [key, value] of Object.entries(props)) {
      if (value === undefined) {
        missing.push(key);
      }
    }
    return missing;
  };

  const envProps: OneTrustConsentProviderEnvProps = {
    environment: env('NEXT_PUBLIC_OURA_ENV') as OneTrustEnvironment,
    segmentKey: env('NEXT_PUBLIC_SEGMENT_KEY'),
    onetrustKey: env('NEXT_PUBLIC_ONETRUST_DOMAIN_SCRIPT'),
  };

  const missingProps = getMissingProps(envProps);
  if (missingProps.length > 0) {
    logger.error('Missing required environment variables', {
      missingProperties: missingProps.join(', '),
      component: 'OneTrustConsentProvider',
    });
    return <>{children}</>;
  }

  return (
    <OneTrustProvider
      applicationName="bulk-order-portal-ui"
      environment={envProps.environment!}
      onetrustKey={envProps.onetrustKey!}
      segmentKey={envProps.segmentKey!}
      onScriptLoad={() => logger.debug('OneTrust ready')}
      onScriptError={(error) => logger.error('OneTrust error', { error })}
    >
      {children}
    </OneTrustProvider>
  );
};

export default OneTrustConsentProvider;
